import { useTheme } from '@mui/material'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateMooringMapCaches } from 'src/api/moorings'
import { DataGridWithServerPagination } from 'src/components/DataGridWithServerPagination'
import { useDialogCtx } from 'src/hooks/context-hooks'
import {
  invalidatePositionDrawingCache,
  positionDrawingEndpoints,
  useDeletePositionDrawing,
  useGetPositionDrawing,
  useUpdatePositionDrawing,
} from '../../../api/position-drawing'
import { CenteredSpinner } from '../../../components/Spinner'
import { IPositionDrawing } from '../../../generated-types/position-drawing'
import { ShowDialogProps } from '../../../show-dialog'
import { getPositionDrawingTableSx, POSITION_DRAWING_ROW_HEIGHT } from './components/positionDrawingTableStyles'
import { usePositionDrawingTableColumns } from './components/usePositionDrawingTableColumns'
import { PositionDrawingForm } from './positionDrawingForm'

export interface UpdatePositionDrawingProps extends ShowDialogProps {
  id: EntityId
}

const UpdatePositionDrawingDialog: React.FC<UpdatePositionDrawingProps> = ({ pop, id }) => {
  const { t } = useTranslation(['admin-position-drawing', 'common'])

  const mutation = useUpdatePositionDrawing(id as EntityId)

  const onSubmit = useCallback(
    async form => {
      if (
        !Array.isArray(form.positionDrawingProducts) ||
        form.positionDrawingProducts.length === 0 ||
        form.positionDrawingProducts.some(x => x.quantity === undefined || x.quantity < 1) ||
        form.positionDrawingProducts.some(x => x.positionDrawingNumber === undefined || x.positionDrawingNumber <= 0)
      ) {
        toast(t('drawingPositionProductValidationError', { ns: 'admin-position-drawing' }), { type: 'error' })
        return
      }
      await mutation.mutateAsync(form, {
        onSuccess: () => {
          pop(true)
          toast.success(t('updatePositionDrawingSuccess', { ns: 'admin-position-drawing' }))
          invalidatePositionDrawingCache.getPositionDrawingById(id)
          invalidateMooringMapCaches()
          invalidatePositionDrawingCache.useGetPositionDrawings()
          toast.success(t('successfullyUpdated', { ns: 'common-api-message' }))
        },
      })
    },
    [id, mutation, pop, t],
  )

  const { data, isLoading } = useGetPositionDrawing(id)
  if (isLoading) return <CenteredSpinner />

  return (
    <PositionDrawingForm
      title={t('updatePositionDrawing')}
      onSubmit={onSubmit}
      onCancel={() => pop()}
      initialValues={data as IPositionDrawing}
    />
  )
}

export interface PositionDrawingTableFilters {
  searchInput?: string
  productCategoryIds?: EntityId[]
}

export const PositionDrawingTable: React.FC<{
  filters: PositionDrawingTableFilters
}> = ({ filters }) => {
  const theme = useTheme()
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { t } = useTranslation(['admin-position-drawing', 'common'])
  const { mutate, isLoading: isDeleting, error } = useDeletePositionDrawing()

  useEffect(() => {
    if (error && error['data'] == 'PositionDrawingHasConnectedToAPosition') {
      toast(t('positionDrawingHasConnectedToAPosition'), { type: 'error' })
    }
  }, [error, t])

  const onEdit = useCallback(
    async (id: EntityId) => {
      const updated = await showDialog(
        UpdatePositionDrawingDialog,
        {
          componentProps: { id },
          opts: {
            maxWidth: 'md',
            fullWidth: true,
          },
          isAutoCloseDisabled: true,
        },
        undefined,
        true,
      )
      if (!updated) return
    },
    [showDialog],
  )

  const onDelete = useCallback(
    async (id: EntityId) =>
      showConfirmDialog(
        t('deletePositionDrawing', { ns: 'admin-position-drawing' }),
        t('areYouSureDeletePositionDrawing', { ns: 'admin-position-drawing' }),
        {
          acceptText: t('yes', { ns: 'common' }),
          cancelText: t('no', { ns: 'common' }),
        },
        async pop => {
          await mutate(id, {
            onSuccess: () => {
              toast.success(t('deletePositionDrawingSuccess', { ns: 'admin-position-drawing' }))
              invalidatePositionDrawingCache.useGetPositionDrawings()
              pop()
            },
          })
        },
      ),
    [mutate, showConfirmDialog, t],
  )

  const columns = usePositionDrawingTableColumns(onEdit, onDelete, isDeleting)

  return (
    <DataGridWithServerPagination
      queryUrl={positionDrawingEndpoints.getPositionDrawings()}
      queryParams={filters}
      columns={columns}
      sx={getPositionDrawingTableSx(theme)}
      getRowHeight={() => POSITION_DRAWING_ROW_HEIGHT}
      onCellClick={cell => {
        if (cell.field === 'actions' || cell.field === 'fileUrl') return
        onEdit(cell.id)
      }}
    />
  )
}
